import { useMemo, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { P, match } from "ts-pattern";
import {
  makeCogRunSnippet,
  makeDockerRunSnippet,
  makeHTTPRunSnippet,
  makeNodeImportAndConfigureClientSnippet,
  makeNodeRunSnippet,
  makePythonImportAndConfigureClientSnippet,
  makePythonRunSnippet,
  makePythonRunSnippetWithStreaming,
  makeVersionlessHTTPRunSnippet,
  makeVersionlessNodeRunSnippet,
  makeVersionlessNodeRunSnippetWithStreaming,
  makeVersionlessPythonRunSnippet,
  makeVersionlessPythonRunSnippetWithStreaming,
} from "../../code-snippets";
import type { SupportedLanguage } from "../../highlight";
import { getOutputSchema, type ObjectWithSchema } from "../../schema";
import type { AccessToken, Model, Version } from "../../types";
import { route } from "../../urls";
import AuthToken from "../auth-token";
import CodeBlock from "../code-block";
import StreamingCodeBlock from "../streaming-code-block";

export enum RunWithContext {
  Cog = "cog",
  Docker = "docker",
  HTTP = "http",
  NodeJS = "nodejs",
  Python = "python",
}

export const runWithHighlightedLanguageMap: {
  [key in RunWithContext]: SupportedLanguage;
} = {
  [RunWithContext.Cog]: "shell",
  [RunWithContext.Docker]: "shell",
  [RunWithContext.HTTP]: "shell",
  [RunWithContext.NodeJS]: "javascript",
  [RunWithContext.Python]: "python",
};

const useFormValues = () => {
  const { getValues } = useFormContext();
  return {
    ...useWatch(), // subscribe to form value updates
    ...getValues(), // always merge with latest form values
  };
};

/**
 *
 * Custom component that reads the current form values
 * from the surrounding React Hook Form context,
 * rather than from an "inputs" prop
 */
export function RunWithAPIPlayground(
  props: {
    model: Model;
    schemaObject: ObjectWithSchema;
    token?: AccessToken | null;
  } & (
    | {
        usesVersionlessApi: false;
        version: Version;
      }
    | {
        usesVersionlessApi: true;
        version?: never;
      }
  ) &
    (
      | {
          context: RunWithContext.Cog | RunWithContext.Docker;
          dockerImageName: string | null;
        }
      | {
          context: Exclude<
            RunWithContext,
            RunWithContext.Cog | RunWithContext.Docker
          >;
          dockerImageName?: never;
        }
    )
) {
  const input = useFormValues();
  return <RunWith {...props} input={input} />;
}

export function RunWith(
  props: ComponentPropsWithoutRef<typeof RunWithAPIPlayground> & {
    input: Record<string, any>;
  }
) {
  return (
    <div className="space-y-lh">
      <InstallDependencies context={props.context} model={props.model} />
      {props.token != null && (
        <SetAuthToken context={props.context} token={props.token} />
      )}
      <ImportAndConfigureClient
        schemaObject={props.schemaObject}
        context={props.context}
        model={props.model}
      />
      <RunPreamble context={props.context} model={props.model} />
      {match(props)
        .with(
          {
            context: P.union(RunWithContext.Cog, RunWithContext.Docker),
            usesVersionlessApi: true,
          },
          (props) => (
            <RunSnippet
              context={props.context}
              dockerImageName={props.dockerImageName}
              input={props.input}
              model={props.model}
              schemaObject={props.schemaObject}
              usesVersionlessApi
            />
          )
        )
        .with(
          {
            context: P.union(RunWithContext.Cog, RunWithContext.Docker),
            usesVersionlessApi: false,
          },
          (props) => (
            <RunSnippet
              context={props.context}
              dockerImageName={props.dockerImageName}
              input={props.input}
              model={props.model}
              schemaObject={props.schemaObject}
              usesVersionlessApi={false}
              version={props.version}
            />
          )
        )
        .with(
          {
            usesVersionlessApi: true,
          },
          (props) => (
            <RunSnippet
              context={props.context}
              dockerImageName={props.dockerImageName}
              input={props.input}
              model={props.model}
              schemaObject={props.schemaObject}
              usesVersionlessApi
            />
          )
        )
        .with(
          {
            usesVersionlessApi: false,
          },
          (props) => (
            <RunSnippet
              context={props.context}
              dockerImageName={props.dockerImageName}
              input={props.input}
              model={props.model}
              schemaObject={props.schemaObject}
              usesVersionlessApi={false}
              version={props.version}
            />
          )
        )
        .exhaustive()}
      <LearnMore context={props.context} />
    </div>
  );
}

export function InstallDependencies({
  context,
  model,
}: {
  context: RunWithContext;
  model: Model;
}) {
  const summary = useMemo<ReactNode>(
    () =>
      match(context)
        .with(RunWithContext.Cog, () => (
          <>
            You can run this model locally using{" "}
            <a href="https://github.com/replicate/cog">Cog</a>. First, install
            Cog:
          </>
        ))
        .with(RunWithContext.Docker, () =>
          model.visibility === "public" ? (
            <>You can run this model locally using Docker.</>
          ) : (
            <>
              You can run this model locally using Docker and{" "}
              <a href="https://github.com/replicate/cog">Cog</a>. First, install
              Cog:
            </>
          )
        )
        .with(RunWithContext.HTTP, () => null)
        .with(RunWithContext.NodeJS, () => (
          <>
            Install{" "}
            <a href="https://github.com/replicate/replicate-javascript">
              Replicate’s Node.js client library
            </a>
            :
          </>
        ))
        .with(RunWithContext.Python, () => (
          <>
            Install{" "}
            <a href="https://github.com/replicate/replicate-python">
              Replicate’s Python client library
            </a>
            :
          </>
        ))
        .exhaustive(),
    [context, model.visibility]
  );
  const body = useMemo<ReactNode>(
    () =>
      match(context)
        .with(RunWithContext.Cog, () => (
          <>
            <CodeBlock language="shell" textContent="brew install cog" />
            <p>
              If you don’t have <a href="https://brew.sh/">Homebrew</a>, there
              are{" "}
              <a href="https://github.com/replicate/cog#install">
                other installation options available
              </a>
              .
            </p>
          </>
        ))
        .with(RunWithContext.Docker, () =>
          model.visibility === "public" ? null : (
            <>
              <CodeBlock language="shell" textContent="brew install cog" />
              <p>
                If you don’t have <a href="https://brew.sh/">Homebrew</a>, there
                are{" "}
                <a href="https://github.com/replicate/cog#install">
                  other installation options available
                </a>
                .
              </p>
            </>
          )
        )
        .with(RunWithContext.HTTP, () => null)
        .with(RunWithContext.NodeJS, () => (
          <CodeBlock language="shell" textContent="npm install replicate" />
        ))
        .with(RunWithContext.Python, () => (
          <CodeBlock language="shell" textContent="pip install replicate" />
        ))
        .exhaustive(),
    [context, model.visibility]
  );

  if (!summary || !body) {
    return null;
  }

  return <InstructionDetails summary={summary}>{body}</InstructionDetails>;
}

export function SetAuthToken({
  context,
  token,
}: {
  context: RunWithContext;
  token: AccessToken | null;
}) {
  return match(context)
    .with(P.union(RunWithContext.Cog, RunWithContext.Docker), () => null)
    .with(
      P.union(
        RunWithContext.HTTP,
        RunWithContext.NodeJS,
        RunWithContext.Python
      ),
      () => (
        <InstructionDetails
          summary={
            <>
              Set the <code>REPLICATE_API_TOKEN</code> environment variable:
            </>
          }
        >
          <AuthToken token={token} type="shell" />
        </InstructionDetails>
      )
    )
    .exhaustive();
}

export function ImportAndConfigureClient({
  context,
  model,
  schemaObject,
}: {
  context: RunWithContext;
  model: Model;
  schemaObject?: ObjectWithSchema;
}) {
  const summary = useMemo<ReactNode>(
    () =>
      match(context)
        .with(P.union(RunWithContext.Cog, RunWithContext.Docker), () =>
          model.visibility === "public" ? null : "Log in with Cog"
        )
        .with(RunWithContext.HTTP, () => null)
        .with(RunWithContext.NodeJS, () => "Import and set up the client:")
        .with(RunWithContext.Python, () => "Import the client:")
        .exhaustive(),
    [context, model.visibility]
  );
  const body = useMemo<ReactNode>(
    () =>
      match(context)
        .with(P.union(RunWithContext.Cog, RunWithContext.Docker), () =>
          model.visibility === "public" ? null : (
            <CodeBlock language="shell" textContent="cog login" />
          )
        )
        .with(RunWithContext.HTTP, () => null)
        .with(RunWithContext.NodeJS, () => (
          <CodeBlock
            language="javascript"
            textContent={makeNodeImportAndConfigureClientSnippet({
              schemaObject,
            })}
          />
        ))
        .with(RunWithContext.Python, () => (
          <CodeBlock
            language="python"
            textContent={makePythonImportAndConfigureClientSnippet()}
          />
        ))
        .exhaustive(),
    [context, model.visibility, schemaObject]
  );

  if (!summary || !body) {
    return null;
  }

  return <InstructionDetails summary={summary}>{body}</InstructionDetails>;
}

function RunPreamble({
  context,
  model,
}: {
  context: RunWithContext;
  model: Model;
}) {
  return match(context)
    .with(P.union(RunWithContext.Cog, RunWithContext.Docker), () => (
      <p>
        Run this to download the model and run it in your local environment:
      </p>
    ))
    .with(
      P.union(
        RunWithContext.HTTP,
        RunWithContext.NodeJS,
        RunWithContext.Python
      ),
      () => (
        <p>
          Run{" "}
          <span className="font-semibold">
            {model.owner}/{model.name}
          </span>{" "}
          using Replicate’s API. Check out the{" "}
          <a
            href={`${route("model_api_reference", {
              username: model.owner,
              name: model.name,
            })}/schema`}
          >
            model's schema
          </a>{" "}
          for an overview of inputs and outputs.
        </p>
      )
    )
    .exhaustive();
}

function RunSnippet({
  context,
  dockerImageName,
  input,
  model,
  schemaObject,
  usesVersionlessApi,
  version,
}: {
  input: Record<string, any>;
  model: Model;
  schemaObject: ObjectWithSchema;
} & (
  | {
      usesVersionlessApi: false;
      version: Version;
    }
  | {
      usesVersionlessApi: true;
      version?: never;
    }
) &
  (
    | {
        context: RunWithContext.Cog | RunWithContext.Docker;
        dockerImageName: string | null;
      }
    | {
        context: Exclude<
          RunWithContext,
          RunWithContext.Cog | RunWithContext.Docker
        >;
        dockerImageName?: never;
      }
  )) {
  const language = runWithHighlightedLanguageMap[context];

  const run = useMemo(
    () =>
      match(context)
        .with(RunWithContext.Cog, () =>
          usesVersionlessApi || dockerImageName == null
            ? null
            : makeCogRunSnippet({
                dockerImageName,
                input,
                schemaObject,
                version,
              })
        )
        .with(RunWithContext.Docker, () =>
          usesVersionlessApi || dockerImageName == null
            ? null
            : makeDockerRunSnippet({
                dockerImageName,
                input,
                schemaObject,
                version,
              })
        )
        .with(RunWithContext.HTTP, () =>
          usesVersionlessApi
            ? makeVersionlessHTTPRunSnippet({
                input,
                model,
                schemaObject,
              })
            : makeHTTPRunSnippet({
                input,
                schemaObject,
                version,
              })
        )
        .with(RunWithContext.NodeJS, () => {
          const output = getOutputSchema(schemaObject);
          const supportsStreaming = output?.["x-cog-array-type"] === "iterator";

          if (usesVersionlessApi) {
            if (supportsStreaming) {
              return (
                <StreamingCodeBlock
                  language="javascript"
                  standardContent={makeVersionlessNodeRunSnippet({
                    input,
                    model,
                    schemaObject,
                  })}
                  streamingContent={makeVersionlessNodeRunSnippetWithStreaming({
                    input,
                    model,
                    schemaObject,
                  })}
                />
              );
            }

            return makeVersionlessNodeRunSnippet({
              input,
              model,
              schemaObject,
            });
          }

          return makeNodeRunSnippet({
            input,
            version,
            schemaObject,
          });
        })
        .with(RunWithContext.Python, () => {
          const output = getOutputSchema(schemaObject);
          const supportsStreaming = output?.["x-cog-array-type"] === "iterator";

          if (usesVersionlessApi) {
            if (supportsStreaming) {
              return (
                <StreamingCodeBlock
                  language="python"
                  standardContent={makeVersionlessPythonRunSnippet({
                    input,
                    model,
                    schemaObject,
                  })}
                  streamingContent={makeVersionlessPythonRunSnippetWithStreaming(
                    {
                      input,
                      model,
                      schemaObject,
                    }
                  )}
                />
              );
            }

            return makeVersionlessPythonRunSnippet({
              input,
              model,
              schemaObject,
            });
          }

          if (supportsStreaming) {
            return (
              <StreamingCodeBlock
                language="python"
                standardContent={makePythonRunSnippet({
                  input,
                  schemaObject,
                  version,
                })}
                streamingContent={makePythonRunSnippetWithStreaming({
                  input,
                  schemaObject,
                  // Models that support streaming in the Python SDK have two
                  // flavors, normal (kuprel/min-dalle) or concatenate
                  // (andreasjansson/plasma)
                  streaming:
                    output?.["x-cog-array-display"] === "concatenate"
                      ? "concatenate"
                      : "entry",
                  version,
                })}
              />
            );
          }

          return makePythonRunSnippet({
            input,
            schemaObject,
            version,
          });
        })
        .exhaustive(),
    [
      context,
      dockerImageName,
      input,
      model,
      schemaObject,
      usesVersionlessApi,
      version,
    ]
  );

  if (
    typeof run === "string" ||
    (Array.isArray(run) && run.every((x) => typeof x === "string"))
  ) {
    return <CodeBlock language={language} textContent={run} />;
  }

  return run;
}

function LearnMore({
  context,
}: {
  context: RunWithContext;
}) {
  return match(context)
    .with(P.union(RunWithContext.Cog, RunWithContext.Docker), () => (
      <p>
        To learn more, take a look at{" "}
        <a href="https://github.com/replicate/cog">the Cog documentation</a>.
      </p>
    ))
    .with(RunWithContext.HTTP, () => (
      <p>
        To learn more, take a look at{" "}
        <a href="https://replicate.com/docs/reference/http">
          Replicate’s HTTP API reference docs
        </a>
        .
      </p>
    ))
    .with(RunWithContext.NodeJS, () => (
      <p>
        To learn more, take a look at{" "}
        <a href="https://replicate.com/docs/get-started/nodejs">
          the guide on getting started with Node.js
        </a>
        .
      </p>
    ))
    .with(RunWithContext.Python, () => (
      <p>
        To learn more, take a look at{" "}
        <a href="https://replicate.com/docs/get-started/python">
          the guide on getting started with Python
        </a>
        .
      </p>
    ))
    .exhaustive();
}

function InstructionDetails({
  summary,
  children,
}: {
  summary: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-lh">
      <div className="space-y-lh">{summary}</div>
      <div className="space-y-lh">{children}</div>
    </div>
  );
}
